import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  ArrowLeft,
  CheckCircle2,
  ClipboardList,
  ChevronLeft,
  Download,
  LayoutDashboard,
  BookOpen,
  BookOpenText,
  Headphones,
  History,
  MailOpen,
  Phone,
  Plus,
  Search,
  ShieldCheck,
  SlidersHorizontal,
  Sparkles,
  UserRound,
  Users,
  X,
} from "lucide-react";
import ConfirmDialog, {
  AmbassadorConfirmation,
} from "../components/ambassadors/ConfirmDialog";
import WeeklyReviews from "../components/ambassadors/WeeklyReviews";
import Applications from "../components/ambassadors/Applications";
import AdminDashboard from "../components/ambassadors/AdminDashboard";
import OperatorGuide from "../components/ambassadors/OperatorGuide";
import { ambassadors } from "../services/ambassadors";
import {
  AmbassadorContext,
  AmbassadorDashboard,
  AmbassadorMaterial,
  AmbassadorMission,
  AmbassadorProfile,
  AmbassadorResources,
  AmbassadorStaff,
  ambassadorDate,
  ambassadorError,
  ambassadorPerson,
  ambassadorRoles,
  ambassadorStatuses,
  materialKinds,
  ambassadorEventLabels,
  ambassadorDay,
  AmbassadorHistoryKind,
} from "../types/ambassadors";
import "./Ambassadors.css";
import AmbassadorUserPicker from "../components/AmbassadorUserPicker";

const fields = (form: HTMLFormElement) =>
  Object.fromEntries(new FormData(form).entries()) as Record<string, string>;
const datetime = (value?: string) =>
  value
    ? new Date(
        new Date(value).getTime() - new Date(value).getTimezoneOffset() * 60000,
      )
        .toISOString()
        .slice(0, 16)
    : "";
const closed = (p: AmbassadorProfile) =>
  ["WITHDRAWN", "DECLINED"].includes(p.status);
const profileIcons: Record<string, typeof Users> = {
  overview: UserRound,
  onboarding: ClipboardList,
  activity: Sparkles,
  support: Headphones,
  weekly: CheckCircle2,
  history: History,
};

export default function Ambassadors() {
  const [context, setContext] = useState<AmbassadorContext>();
  const [dashboard, setDashboard] = useState<AmbassadorDashboard>();
  const [staff, setStaff] = useState<AmbassadorStaff[]>([]);
  const [staffEditing, setStaffEditing] = useState<AmbassadorStaff>();
  const [items, setItems] = useState<AmbassadorProfile[]>([]);
  const [total, setTotal] = useState(0);
  const [pendingApplications, setPendingApplications] = useState(0);
  const [resources, setResources] = useState<AmbassadorResources>({
    materials: [],
    missions: [],
  });
  const [profile, setProfile] = useState<AmbassadorProfile>();
  const [tab, setTab] = useState("dashboard");
  const [query, setQuery] = useState({
    search: "",
    status: "",
    supportId: "",
    skip: 0,
  });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [material, setMaterial] = useState<AmbassadorMaterial>();
  const [mission, setMission] = useState<AmbassadorMission>();
  const [viewVersion, setViewVersion] = useState(0);
  const [profileTab, setProfileTab] = useState("overview");
  const [showCreate, setShowCreate] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const [confirmation, setConfirmation] =
    useState<AmbassadorConfirmation | null>(null);
  const request = useRef(0);
  const mutation = useRef(false);
  const invalidateRequests = useCallback(() => {
    request.current++;
  }, []);
  const feedback = useRef<HTMLDivElement>(null);
  const createPanel = useRef<HTMLElement>(null);
  const profileSummary = useRef<HTMLElement>(null);
  const selectedProfileId = profile?.id;
  useEffect(() => {
    if (selectedProfileId) {
      profileSummary.current?.scrollIntoView?.({
        behavior: "smooth",
        block: "start",
      });
      profileSummary.current?.focus({ preventScroll: true });
    }
  }, [selectedProfileId]);
  useEffect(() => {
    if (showCreate && tab === "profiles" && !profile) {
      createPanel.current?.scrollIntoView?.({
        behavior: "smooth",
        block: "start",
      });
      createPanel.current
        ?.querySelector<HTMLInputElement>('input[name="fullName"]')
        ?.focus({ preventScroll: true });
    }
  }, [showCreate, tab, profile]);
  const has = (role: string) =>
    context?.roles.includes("SUPERVISOR") || context?.roles.includes(role);
  const canProfiles = has("RECRUITER") || has("SUPPORT");
  const params = useCallback(
    () =>
      Object.fromEntries(
        Object.entries(query).filter(
          ([key, value]) => key === "skip" || value !== "",
        ),
      ),
    [query],
  );
  const load = useCallback(async () => {
    const version = ++request.current;
    const ctx = await ambassadors.context();
    if (!ctx.roles.length) {
      if (version === request.current) {
        setContext(ctx);
        setProfile(undefined);
      }
      return;
    }
    const canRead = ctx.roles.some((r) =>
      ["SUPERVISOR", "RECRUITER", "SUPPORT"].includes(r),
    );
    const canReviewApplications = ctx.roles.some((r) =>
      ["SUPERVISOR", "RECRUITER"].includes(r),
    );
    const results = await Promise.all([
      ambassadors.staff(),
      ambassadors.resources(),
      ...(canRead ? [ambassadors.dashboard(), ambassadors.list(params())] : []),
      ...(canReviewApplications
        ? [ambassadors.applications({ status: "PENDING", skip: 0, take: 1 })]
        : []),
    ]);
    if (version !== request.current) return;
    setContext(ctx);
    setStaff(results[0] as AmbassadorStaff[]);
    setResources(results[1] as AmbassadorResources);
    setPendingApplications(
      canReviewApplications ? (results[4] as { total: number }).total : 0,
    );
    if (canRead) {
      setDashboard(results[2] as AmbassadorDashboard);
      const list = results[3] as { items: AmbassadorProfile[]; total: number };
      setItems(list.items);
      setTotal(list.total);
    } else setTab("resources");
  }, [params]);
  useEffect(() => {
    let live = true;
    setLoading(true);
    load()
      .catch((e) => {
        if (live) setError(ambassadorError(e));
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
      invalidateRequests();
    };
  }, [load, invalidateRequests]);
  const run = async (
    action: () => Promise<unknown>,
    form?: HTMLFormElement,
  ) => {
    if (mutation.current) return;
    mutation.current = true;
    setBusy(true);
    setError("");
    setSuccess("");
    try {
      await action();
      form?.reset();
      setSuccess("تغییرات با موفقیت ثبت شد.");
      try {
        await load();
        if (profile) {
          try {
            setProfile(await ambassadors.detail(profile.id));
          } catch (e) {
            if (
              (e as { response?: { status: number } }).response?.status === 404
            )
              setProfile(undefined);
            else throw e;
          }
        }
      } catch {
        setError(
          "تغییرات ثبت شد، اما دریافت آخرین وضعیت انجام نشد. اطلاعات را تازه کنید؛ نیازی به ثبت دوباره نیست.",
        );
      }
      setViewVersion((v) => v + 1);
    } catch (e) {
      setError(ambassadorError(e));
    } finally {
      mutation.current = false;
      setBusy(false);
      feedback.current?.focus();
    }
  };
  const openProfile = async (id: string) => {
    if (mutation.current) return;
    setBusy(true);
    setError("");
    setSuccess("");
    try {
      setProfile(await ambassadors.detail(id));
      setTab("profiles");
      setProfileTab("overview");
      setViewVersion((v) => v + 1);
    } catch (e) {
      setError(ambassadorError(e));
    } finally {
      setBusy(false);
    }
  };
  const more = async (kind: AmbassadorHistoryKind) => {
    if (!profile || mutation.current) return;
    mutation.current = true;
    setBusy(true);
    setError("");
    try {
      const rows = await ambassadors.history(
        profile.id,
        kind,
        (profile[kind] || []).length,
      );
      setProfile((p) =>
        p ? { ...p, [kind]: [...(p[kind] || []), ...rows] } : p,
      );
    } catch (e) {
      setError(ambassadorError(e));
    } finally {
      mutation.current = false;
      setBusy(false);
    }
  };
  const loadMore = (kind: AmbassadorHistoryKind) =>
    profile &&
    (profile[kind]?.length || 0) < (profile.historyCounts?.[kind] || 0) ? (
      <button
        type="button"
        disabled={busy}
        className="amb-secondary"
        onClick={() => more(kind)}
      >
        نمایش موارد قدیمی‌تر
      </button>
    ) : null;
  const refresh = async () => {
    setError("");
    setLoading(true);
    try {
      await load();
      if (profile) setProfile(await ambassadors.detail(profile.id));
    } catch (e) {
      setError(ambassadorError(e));
    } finally {
      setLoading(false);
    }
  };
  const filterForm = (
    <form
      key={`profile-filters:${query.search}:${query.status}:${query.supportId}`}
      className="amb-grid amb-profile-filters"
      onSubmit={(e) => {
        e.preventDefault();
        const f = fields(e.currentTarget);
        setQuery({
          search: f.search,
          status: f.status,
          supportId: f.supportId,
          skip: 0,
        });
      }}
    >
      <label>
        جست‌وجو
        <span className="amb-input-icon">
          <Search size={17} aria-hidden="true" />
          <input
            name="search"
            placeholder="نام، تلفن، شناسه تلگرام یا کد"
            defaultValue={query.search}
          />
        </span>
      </label>
      <label>
        وضعیت
        <select name="status" defaultValue={query.status}>
          <option value="">همه وضعیت‌ها</option>
          {Object.entries(ambassadorStatuses).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </select>
      </label>
      <label>
        پشتیبان
        <select name="supportId" defaultValue={query.supportId}>
          <option value="">همه پشتیبان‌ها</option>
          {staff
            .filter((s) =>
              s.roles.some((r) => ["SUPPORT", "SUPERVISOR"].includes(r)),
            )
            .map((s) => (
              <option value={s.userId} key={s.userId}>
                {ambassadorPerson(s.user)}
              </option>
            ))}
        </select>
      </label>
      <div className="amb-row amb-filter-actions">
        <button disabled={busy || loading}>
          <SlidersHorizontal size={16} aria-hidden="true" />
          اعمال فیلتر
        </button>
        <button
          type="button"
          disabled={busy || loading}
          className="amb-secondary"
          onClick={(e) => {
            e.currentTarget.form?.reset();
            setQuery({ search: "", status: "", supportId: "", skip: 0 });
          }}
        >
          <X size={16} aria-hidden="true" />
          پاک کردن فیلترها
        </button>
        <button
          type="button"
          disabled={busy}
          className="amb-secondary"
          onClick={() => run(() => ambassadors.export(params()))}
        >
          <Download size={16} aria-hidden="true" />
          خروجی اکسل
        </button>
      </div>
    </form>
  );
  return (
    <main
      className="amb amb-admin"
      aria-busy={busy || loading}
      onClickCapture={(e) => {
        if (busy) {
          e.preventDefault();
          e.stopPropagation();
        }
      }}
      data-profile-tab={profileTab}
      onSubmitCapture={(e) => {
        if (busy) {
          e.preventDefault();
          e.stopPropagation();
        }
      }}
    >
      <div className="amb-topline">
        <div className="amb-breadcrumb">
          <span>پنل مدیریت</span>
          <ChevronLeft size={13} aria-hidden="true" />
          <strong>سفیران من حقیقی</strong>
        </div>
        {!!context?.roles.length && (
          <span className="amb-role-pill">
            <ShieldCheck size={14} aria-hidden="true" />
            {context.roles.map((r) => ambassadorRoles[r]).join(" · ")}
          </span>
        )}
      </div>
      <header className="amb-hero">
        <div className="amb-hero-title">
          <span className="amb-brand-mark">
            <Users size={27} aria-hidden="true" />
          </span>
          <div>
            <h1>مدیریت سفیران</h1>
            <p>از اولین درخواست تا همراهی در مسیر همکاری</p>
          </div>
        </div>
        {!!context?.roles.length && (
          <div className="amb-hero-actions">
            <button
              type="button"
              disabled={busy}
              className="amb-secondary"
              aria-haspopup="dialog"
              onClick={() => setGuideOpen(true)}
            >
              <BookOpenText size={17} aria-hidden="true" />
              راهنمای پنل
            </button>
            {has("RECRUITER") && (
              <>
                <button
                  type="button"
                  disabled={busy}
                  className="amb-secondary"
                  onClick={() => {
                    setProfile(undefined);
                    setTab("applications");
                  }}
                >
                  <MailOpen size={17} aria-hidden="true" />
                  بررسی درخواست‌ها
                  <span className="amb-count">
                    {pendingApplications.toLocaleString("fa-IR")}
                  </span>
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => {
                    setProfile(undefined);
                    setTab("profiles");
                    setShowCreate(true);
                  }}
                >
                  <Plus size={18} aria-hidden="true" />
                  داوطلب جدید
                </button>
              </>
            )}
          </div>
        )}
      </header>
      {loading && context && (
        <div className="amb-sync-notice" role="status">
          در حال به‌روزرسانی اطلاعات…
        </div>
      )}
      <div ref={feedback} tabIndex={-1} className="amb-feedback">
        {error && (
          <div role="alert" className="amb-error">
            {error}
            <button
              disabled={busy || loading}
              className="amb-secondary"
              onClick={refresh}
            >
              تازه‌سازی اطلاعات
            </button>
          </div>
        )}
        {success && (
          <div role="status" className="amb-success">
            <CheckCircle2 size={18} /> {success}
          </div>
        )}
      </div>
      {loading && !context ? (
        <div
          className="amb-skeleton"
          role="status"
          aria-label="در حال دریافت اطلاعات"
        >
          <div />
          <div />
          <div />
        </div>
      ) : context && !context.roles.length ? (
        <section className="amb-card">
          <h2>دسترسی تیم سفیران ندارید</h2>
          <p>مدیر سامانه باید نقش شما را در این بخش تعیین کند.</p>
        </section>
      ) : (
        context && (
          <>
            <nav className="amb-tabs" aria-label="مدیریت سفیران">
              {has("RECRUITER") && (
                <button
                  className={tab === "applications" ? "" : "amb-secondary"}
                  aria-pressed={tab === "applications"}
                  disabled={busy}
                  onClick={() => {
                    setProfile(undefined);
                    setTab("applications");
                  }}
                >
                  <MailOpen size={17} aria-hidden="true" />
                  درخواست‌های عضویت
                  <span
                    className="amb-count"
                    aria-label={`${pendingApplications} درخواست در انتظار بررسی`}
                  >
                    {pendingApplications.toLocaleString("fa-IR")}
                  </span>
                </button>
              )}
              {canProfiles && (
                <>
                  <button
                    className={tab === "dashboard" ? "" : "amb-secondary"}
                    aria-pressed={tab === "dashboard"}
                    disabled={busy}
                    onClick={() => {
                      setProfile(undefined);
                      setTab("dashboard");
                    }}
                  >
                    <LayoutDashboard size={17} aria-hidden="true" />
                    داشبورد و پیگیری
                  </button>
                  <button
                    className={tab === "profiles" ? "" : "amb-secondary"}
                    aria-pressed={tab === "profiles"}
                    disabled={busy}
                    onClick={() => {
                      setProfile(undefined);
                      setTab("profiles");
                    }}
                  >
                    <Users size={17} aria-hidden="true" />
                    پرونده‌ها
                  </button>
                </>
              )}
              <button
                className={tab === "resources" ? "" : "amb-secondary"}
                aria-pressed={tab === "resources"}
                disabled={busy}
                onClick={() => {
                  setProfile(undefined);
                  setTab("resources");
                }}
              >
                <BookOpen size={17} aria-hidden="true" />
                محتوا و مأموریت
              </button>
              {has("SUPERVISOR") && (
                <button
                  className={tab === "staff" ? "" : "amb-secondary"}
                  aria-pressed={tab === "staff"}
                  disabled={busy}
                  onClick={() => {
                    setProfile(undefined);
                    setTab("staff");
                  }}
                >
                  <ShieldCheck size={17} aria-hidden="true" />
                  تیم و دسترسی‌ها
                </button>
              )}
              <button
                type="button"
                className="amb-secondary"
                aria-haspopup="dialog"
                disabled={busy}
                onClick={() => setGuideOpen(true)}
              >
                <BookOpenText size={17} aria-hidden="true" />
                راهنما
              </button>
            </nav>
            {tab === "applications" && has("RECRUITER") && (
              <Applications
                staff={staff}
                onReviewed={load}
                onOpenProfile={openProfile}
              />
            )}
            {tab === "dashboard" && dashboard && (
              <AdminDashboard
                dashboard={dashboard}
                context={context}
                busy={busy}
                canSupport={!!has("SUPPORT")}
                viewVersion={viewVersion}
                onOpenProfile={openProfile}
                run={run}
              />
            )}
            {tab === "profiles" && !profile && (
              <>
                <section className="amb-card amb-profiles-panel">
                  <div className="amb-section-title">
                    <div>
                      <span className="amb-eyebrow">
                        پرونده‌های قابل دسترسی شما
                      </span>
                      <h2>فهرست سفیران</h2>
                    </div>
                    {has("RECRUITER") && (
                      <button
                        disabled={busy}
                        onClick={() => setShowCreate((v) => !v)}
                      >
                        <Plus size={17} />{" "}
                        {showCreate ? "بستن فرم جدید" : "ثبت داوطلب"}
                      </button>
                    )}
                  </div>
                  {filterForm}
                  <div className="amb-table-caption">
                    <span>{total.toLocaleString("fa-IR")} پرونده</span>
                    <span className="amb-muted">
                      {query.search || query.status || query.supportId
                        ? "نتایج مطابق فیلترهای انتخاب‌شده"
                        : "تمام پرونده‌های قابل دسترسی شما"}
                    </span>
                  </div>
                  <div className="amb-scroll">
                    <table className="amb-profiles-table">
                      <thead>
                        <tr>
                          <th>نام</th>
                          <th>وضعیت</th>
                          <th>پشتیبان</th>
                          <th>آخرین فعالیت</th>
                          <th>عملیات</th>
                        </tr>
                      </thead>
                      <tbody>
                        {items.map((p) => (
                          <tr key={p.id}>
                            <td data-label="نام">
                              <div className="amb-table-person">
                                <span className="amb-avatar" aria-hidden="true">
                                  {Array.from(p.fullName.trim())[0]}
                                </span>
                                <span>
                                  <strong>{p.fullName}</strong>
                                  <small dir="ltr">
                                    {p.phone || "شماره ثبت نشده"}
                                  </small>
                                </span>
                              </div>
                            </td>
                            <td data-label="وضعیت">
                              <span
                                className="amb-badge"
                                data-status={p.status}
                              >
                                {ambassadorStatuses[p.status]}
                              </span>
                            </td>
                            <td data-label="پشتیبان">
                              {ambassadorPerson(p.support)}
                            </td>
                            <td data-label="آخرین فعالیت">
                              {ambassadorDate(p.lastActivityAt)}
                            </td>
                            <td data-label="عملیات">
                              <button
                                disabled={busy}
                                className="amb-table-action"
                                onClick={() => openProfile(p.id)}
                              >
                                مشاهده پرونده
                                <ArrowLeft size={15} aria-hidden="true" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {!items.length && (
                    <div className="amb-empty">
                      <Users size={36} aria-hidden="true" />
                      <h3>پرونده‌ای با این شرایط پیدا نشد.</h3>
                      <p>
                        فیلترها را تغییر دهید یا یک داوطلب جدید به فهرست اضافه
                        کنید.
                      </p>
                    </div>
                  )}
                  <div className="amb-row amb-table-pagination">
                    <button
                      disabled={busy || !query.skip}
                      className="amb-secondary"
                      onClick={() =>
                        setQuery((q) => ({
                          ...q,
                          skip: Math.max(0, q.skip - 30),
                        }))
                      }
                    >
                      قبلی
                    </button>
                    <span className="amb-muted">
                      {total > 0
                        ? `${(query.skip + 1).toLocaleString("fa-IR")} تا ${Math.min(query.skip + 30, total).toLocaleString("fa-IR")} از ${total.toLocaleString("fa-IR")} پرونده`
                        : "۰ پرونده"}
                    </span>
                    <button
                      disabled={busy || query.skip + 30 >= total}
                      className="amb-secondary"
                      onClick={() =>
                        setQuery((q) => ({ ...q, skip: q.skip + 30 }))
                      }
                    >
                      بعدی
                    </button>
                  </div>
                </section>
                {has("RECRUITER") && showCreate && (
                  <section
                    className="amb-card amb-create-panel"
                    ref={createPanel}
                  >
                    <div className="amb-heading-with-icon">
                      <span className="amb-heading-icon">
                        <Plus size={20} aria-hidden="true" />
                      </span>
                      <div>
                        <h2>ثبت داوطلب جدید</h2>
                        <p className="amb-muted">
                          اطلاعات اولیه را وارد کنید؛ تکمیل مراحل از پرونده سفیر
                          ادامه پیدا می‌کند.
                        </p>
                      </div>
                    </div>
                    <form
                      key={`new-profile:${viewVersion}`}
                      onSubmit={(e) => {
                        e.preventDefault();
                        const form = e.currentTarget;
                        const f = fields(form);
                        run(async () => {
                          const response = await ambassadors.create(f);
                          setProfile(
                            await ambassadors.detail(response.data.id),
                          );
                          setProfileTab("onboarding");
                          setShowCreate(false);
                        }, form);
                      }}
                    >
                      <div className="amb-grid">
                        <label>
                          نام و نام خانوادگی
                          <input name="fullName" required maxLength={150} />
                        </label>
                        <label>
                          تلفن
                          <input name="phone" maxLength={60} dir="ltr" />
                        </label>
                        <label>
                          شناسه تلگرام
                          <input
                            name="telegramUsername"
                            maxLength={100}
                            dir="ltr"
                          />
                        </label>
                        <label>
                          منبع معرفی
                          <input name="source" maxLength={150} />
                        </label>
                        <div>
                          <span>شناسه کاربر سایت (اختیاری)</span>
                          <AmbassadorUserPicker name="userId" />
                        </div>
                        <label>
                          پشتیبان
                          <select name="supportId">
                            <option value="">بعداً تعیین می‌شود</option>
                            {staff
                              .filter((s) =>
                                s.roles.some((r) =>
                                  ["SUPPORT", "SUPERVISOR"].includes(r),
                                ),
                              )
                              .map((s) => (
                                <option key={s.userId} value={s.userId}>
                                  {ambassadorPerson(s.user)}
                                </option>
                              ))}
                          </select>
                        </label>
                      </div>
                      <button disabled={busy}>ایجاد پرونده</button>
                    </form>
                  </section>
                )}
              </>
            )}
            {tab === "profiles" && profile && (
              <>
                <button
                  className="amb-secondary"
                  disabled={busy}
                  onClick={() => setProfile(undefined)}
                >
                  <ArrowLeft size={16} /> بازگشت به فهرست
                </button>
                <section
                  className="amb-card amb-profile-summary"
                  ref={profileSummary}
                  tabIndex={-1}
                >
                  <div className="amb-profile-heading">
                    <span
                      className="amb-avatar amb-profile-avatar"
                      aria-hidden="true"
                    >
                      {Array.from(profile.fullName.trim())[0]}
                    </span>
                    <div>
                      <span className="amb-eyebrow">پرونده سفیر</span>
                      <h2>{profile.fullName}</h2>
                      <div className="amb-profile-contacts">
                        {profile.phone && (
                          <a href={`tel:${profile.phone}`}>
                            <Phone size={14} aria-hidden="true" />
                            <span dir="ltr">{profile.phone}</span>
                          </a>
                        )}
                        {profile.telegramUsername && (
                          <span dir="ltr">{profile.telegramUsername}</span>
                        )}
                      </div>
                    </div>
                    <span className="amb-badge" data-status={profile.status}>
                      {ambassadorStatuses[profile.status]}
                    </span>
                  </div>
                  <dl className="amb-summary-grid">
                    <div>
                      <dt>پشتیبان پرونده</dt>
                      <dd>{ambassadorPerson(profile.support)}</dd>
                    </div>
                    <div>
                      <dt>اعضای کانال</dt>
                      <dd>{profile.memberCount.toLocaleString("fa-IR")} نفر</dd>
                    </div>
                    <div>
                      <dt>تاریخ پذیرش</dt>
                      <dd>{ambassadorDate(profile.acceptedAt)}</dd>
                    </div>
                    <div>
                      <dt>پیگیری بعدی</dt>
                      <dd>{ambassadorDate(profile.nextFollowUpAt)}</dd>
                    </div>
                  </dl>
                </section>
                <nav
                  className="amb-row amb-profile-nav"
                  aria-label="بخش‌های پرونده"
                >
                  {Object.entries({
                    overview: "اطلاعات و پیگیری",
                    onboarding: "مراحل ورود",
                    activity: "گزارش فعالیت",
                    support: "درخواست‌ها",
                    weekly: "ارزیابی هفتگی",
                    history: "سابقه",
                  }).map(([key, title]) => {
                    const Icon = profileIcons[key] || UserRound;
                    return (
                      <button
                        type="button"
                        key={key}
                        disabled={busy}
                        className={profileTab === key ? "" : "amb-secondary"}
                        aria-pressed={profileTab === key}
                        onClick={() => setProfileTab(key)}
                      >
                        <Icon size={16} aria-hidden="true" />
                        {title}
                      </button>
                    );
                  })}
                </nav>
                {!closed(profile) && (
                  <aside className="amb-next">
                    <div>
                      <span className="amb-eyebrow">آمادگی پرونده</span>
                      <h2>
                        {context.onboarding.filter(
                          (item) =>
                            item.required &&
                            !profile.checklist.some(
                              (c) => c.key === item.key && c.completed,
                            ),
                        ).length
                          ? `${context.onboarding.filter((item) => item.required && !profile.checklist.some((c) => c.key === item.key && c.completed)).length.toLocaleString("fa-IR")} مرحله ضروری باقی مانده`
                          : "مراحل ضروری تکمیل شده است"}
                      </h2>
                      <p>
                        {!profile.userId
                          ? "حساب کاربری سایت را متصل کنید. "
                          : ""}
                        {!profile.supportId
                          ? "پشتیبان پرونده را تعیین کنید. "
                          : ""}
                        {!profile.channelUrl
                          ? "لینک کانال هنوز ثبت نشده است. "
                          : ""}
                        هدف ۱۰۰ عضو، شرط فعال‌سازی نیست.
                      </p>
                    </div>
                    <button
                      disabled={busy}
                      className="amb-secondary"
                      onClick={() => setProfileTab("onboarding")}
                    >
                      بررسی مراحل <ClipboardList size={16} />
                    </button>
                  </aside>
                )}
                <section
                  className="amb-card"
                  hidden={profileTab !== "overview"}
                >
                  <div className="amb-heading-with-icon">
                    <span className="amb-heading-icon">
                      <UserRound size={19} aria-hidden="true" />
                    </span>
                    <div>
                      <h2>اطلاعات پایه و ارتباطی</h2>
                      <p className="amb-muted">
                        مشخصات، مسیر ارتباط و برنامه پیگیری سفیر
                      </p>
                    </div>
                  </div>
                  <form
                    key={`${profile.id}:${viewVersion}`}
                    onSubmit={(e) => {
                      e.preventDefault();
                      const form = e.currentTarget;
                      const f = fields(form);
                      const {
                        nextFollowUpAt,
                        channelUrl,
                        memberCount,
                        ...rest
                      } = f;
                      run(() =>
                        ambassadors.update(profile.id, {
                          ...rest,
                          memberCount: Number(memberCount),
                          channelUrl: channelUrl || null,
                          nextFollowUpAt: nextFollowUpAt
                            ? new Date(nextFollowUpAt).toISOString()
                            : null,
                        }),
                      );
                    }}
                  >
                    <fieldset disabled={busy || closed(profile)}>
                      <div className="amb-grid">
                        <label>
                          نام
                          <input
                            name="fullName"
                            required
                            defaultValue={profile.fullName}
                          />
                        </label>
                        <label>
                          تلفن
                          <input
                            name="phone"
                            defaultValue={profile.phone}
                            dir="ltr"
                          />
                        </label>
                        <label>
                          تلگرام
                          <input
                            name="telegramUsername"
                            defaultValue={profile.telegramUsername}
                            dir="ltr"
                          />
                        </label>
                        <label>
                          منبع معرفی
                          <input name="source" defaultValue={profile.source} />
                        </label>
                        {has("RECRUITER") && (
                          <>
                            <div>
                              <span>شناسه کاربر سایت</span>
                              <AmbassadorUserPicker
                                name="userId"
                                defaultValue={profile.userId}
                                defaultLabel={ambassadorPerson(profile.user)}
                              />
                            </div>
                            <label>
                              پشتیبان
                              <select
                                name="supportId"
                                defaultValue={profile.supportId || ""}
                              >
                                <option value="">تعیین نشده</option>
                                {profile.support &&
                                  !staff.some(
                                    (s) => s.userId === profile.supportId,
                                  ) && (
                                    <option value={profile.supportId}>
                                      {ambassadorPerson(profile.support)}
                                    </option>
                                  )}
                                {staff
                                  .filter((s) =>
                                    s.roles.some((r) =>
                                      ["SUPPORT", "SUPERVISOR"].includes(r),
                                    ),
                                  )
                                  .map((s) => (
                                    <option key={s.userId} value={s.userId}>
                                      {ambassadorPerson(s.user)}
                                    </option>
                                  ))}
                              </select>
                            </label>
                          </>
                        )}
                        <label>
                          لینک کانال
                          <input
                            type="url"
                            name="channelUrl"
                            defaultValue={profile.channelUrl}
                            dir="ltr"
                          />
                        </label>
                        <label>
                          تعداد اعضا
                          <input
                            type="number"
                            name="memberCount"
                            min={0}
                            max={100000000}
                            required
                            defaultValue={profile.memberCount}
                          />
                        </label>
                        <label>
                          پیگیری بعدی (ساعت دستگاه)
                          <input
                            type="datetime-local"
                            name="nextFollowUpAt"
                            defaultValue={datetime(profile.nextFollowUpAt)}
                          />
                        </label>
                      </div>
                      <button>ذخیره پرونده</button>
                    </fieldset>
                  </form>
                </section>
                {profile.status === "RECRUITING" && (
                  <section
                    className="amb-card"
                    hidden={profileTab !== "onboarding"}
                  >
                    <h2>ثبت نتیجه پذیرش</h2>
                    <p>{context.consent}</p>
                    <p className="amb-muted">
                      فقط پس از توضیح مسئولیت‌ها و دریافت پاسخ آگاهانه، نتیجه را
                      ثبت کنید.
                    </p>
                    <div className="amb-row">
                      <button
                        disabled={busy}
                        onClick={() =>
                          run(() => ambassadors.consent(profile.id, true))
                        }
                      >
                        موافقت دریافت شد
                      </button>
                      <button
                        disabled={busy}
                        className="amb-secondary"
                        onClick={() =>
                          setConfirmation({
                            title: "توقف جذب این داوطلب",
                            description:
                              "عدم تمایل داوطلب ثبت و فرآیند جذب بسته می‌شود. این گزینه را فقط مطابق پاسخ خود فرد انتخاب کنید.",
                            label: "ثبت عدم تمایل",
                            danger: true,
                            action: () =>
                              run(() => ambassadors.consent(profile.id, false)),
                          })
                        }
                      >
                        رد همکاری؛ توقف جذب
                      </button>
                    </div>
                  </section>
                )}
                <section
                  className="amb-card"
                  hidden={profileTab !== "onboarding"}
                >
                  <h2>چک‌لیست ورود</h2>
                  <p className="amb-muted">
                    هدف ۱۰۰ عضو شرط فعال‌سازی نیست. هر تأیید همراه با زمان و
                    شناسه مسئول ثبت می‌شود.
                  </p>
                  {context.onboarding.map((item) => {
                    const c = profile.checklist.find(
                      (check) => check.key === item.key,
                    );
                    return (
                      <label className="amb-check" key={item.key}>
                        <input
                          type="checkbox"
                          checked={!!c?.completed}
                          disabled={
                            busy ||
                            !has("SUPPORT") ||
                            !profile.acceptedAt ||
                            closed(profile) ||
                            item.key === "consent"
                          }
                          onChange={(e) =>
                            run(() =>
                              ambassadors.checklist(
                                profile.id,
                                item.key,
                                e.target.checked,
                              ),
                            )
                          }
                        />
                        {item.title}
                        {item.required && <small> · ضروری</small>}
                        {c?.completedAt && (
                          <div className="amb-muted">
                            {ambassadorDate(c.completedAt)} · مسئول:{" "}
                            {ambassadorPerson(
                              staff.find((s) => s.userId === c.completedBy)
                                ?.user,
                            )}
                          </div>
                        )}
                      </label>
                    );
                  })}
                </section>
                {!closed(profile) && (
                  <>
                    {has("SUPPORT") && (
                      <section
                        className="amb-card"
                        hidden={profileTab !== "overview"}
                      >
                        <h2>تغییر وضعیت</h2>
                        <form
                          onSubmit={(e) => {
                            e.preventDefault();
                            const form = e.currentTarget;
                            const f = fields(form);
                            const action = () =>
                              run(
                                () =>
                                  ambassadors.status(
                                    profile.id,
                                    f.status,
                                    f.note,
                                  ),
                                form,
                              );
                            if (f.status === "WITHDRAWN")
                              setConfirmation({
                                title: "ثبت پایان همکاری",
                                description:
                                  "دسترسی ثبت فعالیت و محتوای سفیر بسته می‌شود. حذف دسترسی کانال‌ها را پس از ثبت پیگیری کنید.",
                                label: "ثبت انصراف",
                                danger: true,
                                action,
                              });
                            else action();
                          }}
                        >
                          <label>
                            وضعیت بعدی
                            <select name="status" required defaultValue="">
                              <option value="">
                                وضعیت بعدی را انتخاب کنید
                              </option>
                              {[
                                "ACTIVE",
                                "NEEDS_SUPPORT",
                                "PAUSED",
                                "WITHDRAWN",
                              ]
                                .filter((s) => s !== profile.status)
                                .map((s) => (
                                  <option key={s} value={s}>
                                    {ambassadorStatuses[s]}
                                  </option>
                                ))}
                            </select>
                          </label>
                          <label>
                            دلیل و اقدام بعدی
                            <textarea name="note" required maxLength={2000} />
                          </label>
                          <p className="amb-muted">
                            فعال‌سازی نیازمند تکمیل چک‌لیست، اتصال حساب سایت،
                            تعیین پشتیبان و لینک کانال است. هنگام انصراف، حذف
                            دسترسی‌های تلگرام را هم پیگیری کنید.
                          </p>
                          <button disabled={busy}>ثبت تغییر وضعیت</button>
                        </form>
                      </section>
                    )}
                    <section
                      className="amb-card"
                      hidden={profileTab !== "overview"}
                    >
                      <h2>ثبت تماس و پیگیری</h2>
                      <form
                        onSubmit={(e) => {
                          e.preventDefault();
                          const form = e.currentTarget;
                          const f = fields(form);
                          run(
                            () => ambassadors.note(profile.id, f.kind, f.note),
                            form,
                          );
                        }}
                      >
                        <label>
                          نوع رویداد
                          <select name="kind">
                            <option value="NOTE">یادداشت داخلی</option>
                            <option value="CALL_SUCCESS">تماس موفق</option>
                            <option value="CALL_NO_ANSWER">بدون پاسخ</option>
                            <option value="CALL_FAILED">تماس ناموفق</option>
                            <option value="SUPPORT_DAILY">
                              پیگیری روزانه پشتیبان
                            </option>
                          </select>
                        </label>
                        <label>
                          نتیجه و اقدام بعدی
                          <textarea name="note" required maxLength={4000} />
                        </label>
                        <button disabled={busy}>ثبت در سابقه</button>
                      </form>
                    </section>
                  </>
                )}
                {closed(profile) && (
                  <section
                    className="amb-card"
                    hidden={profileTab !== "support"}
                  >
                    <h2>پیگیری پایان همکاری</h2>
                    <p>
                      حذف دسترسی کانال‌ها و درخواست‌های مربوط به اطلاعات را
                      پیگیری و نتیجه را ثبت کنید.
                    </p>
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        const form = e.currentTarget;
                        const f = fields(form);
                        run(
                          () => ambassadors.note(profile.id, "NOTE", f.note),
                          form,
                        );
                      }}
                    >
                      <label>
                        نتیجه پیگیری
                        <textarea name="note" required maxLength={4000} />
                      </label>
                      <button disabled={busy}>ثبت در سابقه</button>
                    </form>
                  </section>
                )}
                <section
                  className="amb-card"
                  hidden={profileTab !== "activity"}
                >
                  <h2>گزارش‌های فعالیت</h2>
                  {profile.reports?.map((r) => (
                    <article className="amb-card" key={r.id}>
                      <h3>
                        {ambassadorDay(r.day)}{" "}
                        {r.mission && `· ${r.mission.title}`}
                      </h3>
                      <p className="amb-pre">{r.summary}</p>
                      <p>
                        انتشار: {r.publishedCount} · تعامل معنادار:{" "}
                        {r.meaningfulInteractions}
                      </p>
                      {r.evidenceUrl && (
                        <a
                          href={r.evidenceUrl}
                          target="_blank"
                          rel="noreferrer"
                        >
                          شاهد فعالیت
                        </a>
                      )}
                      {r.feedback && (
                        <p className="amb-message amb-pre">
                          کیفیت: {r.quality} از ۵ · {r.feedback}
                        </p>
                      )}
                      {has("SUPPORT") && (
                        <form
                          onSubmit={(e) => {
                            e.preventDefault();
                            const form = e.currentTarget;
                            const f = fields(form);
                            run(
                              () =>
                                ambassadors.review(r.id, {
                                  feedback: f.feedback,
                                  quality: Number(f.quality),
                                }),
                              form,
                            );
                          }}
                        >
                          <label>
                            کیفیت محتوا (۱ تا ۵)
                            <input
                              type="number"
                              name="quality"
                              required
                              min={1}
                              max={5}
                              defaultValue={r.quality || 3}
                            />
                          </label>
                          <label>
                            بازخورد
                            <textarea
                              name="feedback"
                              required
                              maxLength={4000}
                              defaultValue={r.feedback}
                            />
                          </label>
                          <button disabled={busy}>ثبت ارزیابی</button>
                        </form>
                      )}
                    </article>
                  ))}
                  {!profile.reports?.length && <p>هنوز گزارشی ثبت نشده است.</p>}
                  {loadMore("reports")}
                </section>
                <section className="amb-card" hidden={profileTab !== "support"}>
                  <h2>درخواست‌های پشتیبانی</h2>
                  {profile.tickets?.map((t) => (
                    <article className="amb-card" key={t.id}>
                      <h3>
                        {t.subject} · {t.resolvedAt ? "رسیدگی‌شده" : "باز"}
                      </h3>
                      {t.replies.map((r) => (
                        <div className="amb-message" key={r.id}>
                          <small>
                            {r.actorId === profile.userId
                              ? "سفیر"
                              : "تیم پشتیبانی"}{" "}
                            · {ambassadorDate(r.createdAt)}
                          </small>
                          <p className="amb-pre">{r.body}</p>
                        </div>
                      ))}
                      {has("SUPPORT") && (
                        <>
                          <form
                            onSubmit={(e) => {
                              e.preventDefault();
                              const form = e.currentTarget;
                              const f = fields(form);
                              run(
                                () => ambassadors.reply(t.id, { body: f.body }),
                                form,
                              );
                            }}
                          >
                            <label>
                              پاسخ
                              <textarea name="body" required maxLength={4000} />
                            </label>
                            <button disabled={busy}>ارسال پاسخ</button>
                          </form>
                          <div className="amb-row">
                            <button
                              disabled={busy}
                              className="amb-secondary"
                              onClick={() =>
                                run(() =>
                                  ambassadors.reply(t.id, {
                                    resolved: !t.resolvedAt,
                                  }),
                                )
                              }
                            >
                              {t.resolvedAt
                                ? "بازگشایی درخواست"
                                : "علامت‌گذاری رسیدگی‌شده"}
                            </button>
                          </div>
                        </>
                      )}
                    </article>
                  ))}
                  {!profile.tickets?.length && <p>درخواستی ثبت نشده است.</p>}
                  {loadMore("tickets")}
                </section>
                {profileTab === "weekly" && (
                  <>
                    <WeeklyReviews
                      profile={profile}
                      canReview={!!has("SUPERVISOR")}
                      busy={busy}
                      run={run}
                    />
                    {loadMore("weeklyReviews")}
                  </>
                )}
                <section
                  className="amb-card amb-history"
                  hidden={profileTab !== "history"}
                >
                  <h2>سابقه پرونده</h2>
                  {profile.events?.map((event) => (
                    <div className="amb-check" key={event.id}>
                      <small className="amb-muted">
                        {ambassadorDate(event.createdAt)} ·{" "}
                        {ambassadorEventLabels[event.kind] || "رویداد پرونده"}
                      </small>
                      <p className="amb-pre">{event.note}</p>
                    </div>
                  ))}
                  {loadMore("events")}
                </section>
              </>
            )}
            {tab === "resources" && (
              <>
                <section className="amb-card">
                  <div className="amb-section-title">
                    <h2>آموزش، محتوا و کانال‌های مرجع</h2>
                    {has("CONTENT") && (
                      <button
                        className="amb-secondary"
                        disabled={busy}
                        onClick={() => run(() => ambassadors.starterKit())}
                      >
                        افزودن بسته آغازین SOP
                      </button>
                    )}
                  </div>
                  <p className="amb-muted">
                    بسته آغازین شامل آموزش و پیام‌های استاندارد است و به‌صورت
                    پیش‌نویس اضافه می‌شود. پیش از انتشار، آن را با اطلاعات
                    مجموعه تطبیق دهید.
                  </p>
                  {resources.materials.map((m) => (
                    <article className="amb-card" key={m.id}>
                      <span className="amb-badge">
                        {materialKinds[m.kind]} ·{" "}
                        {m.published ? "منتشرشده" : "پیش‌نویس"}
                      </span>
                      <h3>{m.title}</h3>
                      <p className="amb-pre">{m.body}</p>
                      {m.url && (
                        <a href={m.url} target="_blank" rel="noreferrer">
                          مشاهده لینک
                        </a>
                      )}
                      {has("CONTENT") && (
                        <div className="amb-row">
                          <button
                            disabled={busy}
                            className="amb-secondary"
                            onClick={() => setMaterial(m)}
                          >
                            ویرایش
                          </button>
                          <button
                            disabled={busy}
                            className="amb-secondary"
                            onClick={() =>
                              run(() =>
                                ambassadors.material(
                                  {
                                    kind: m.kind,
                                    title: m.title,
                                    body: m.body,
                                    ...(m.url ? { url: m.url } : {}),
                                    published: !m.published,
                                  },
                                  m.id,
                                ),
                              )
                            }
                          >
                            {m.published ? "برداشتن از انتشار" : "انتشار"}
                          </button>
                        </div>
                      )}
                    </article>
                  ))}
                  {!resources.materials.length && <p>محتوایی ثبت نشده است.</p>}
                </section>
                {has("CONTENT") && (
                  <section className="amb-card">
                    <h2>{material ? "ویرایش محتوا" : "محتوای جدید"}</h2>
                    <form
                      key={material?.id || `new-material:${viewVersion}`}
                      onSubmit={(e) => {
                        e.preventDefault();
                        const form = e.currentTarget;
                        const f = fields(form);
                        run(async () => {
                          await ambassadors.material(
                            {
                              kind: f.kind,
                              title: f.title,
                              body: f.body,
                              ...(f.url ? { url: f.url } : { url: null }),
                              published: f.published === "on",
                            },
                            material?.id,
                          );
                          setMaterial(undefined);
                        }, form);
                      }}
                    >
                      <label>
                        نوع
                        <select
                          name="kind"
                          defaultValue={material?.kind || "TRAINING"}
                        >
                          {Object.entries(materialKinds).map(([k, v]) => (
                            <option key={k} value={k}>
                              {v}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label>
                        عنوان
                        <input
                          name="title"
                          required
                          maxLength={200}
                          defaultValue={material?.title}
                        />
                      </label>
                      <label>
                        متن
                        <textarea
                          name="body"
                          required
                          maxLength={20000}
                          defaultValue={material?.body}
                        />
                      </label>
                      <label>
                        لینک اختیاری
                        <input
                          type="url"
                          name="url"
                          dir="ltr"
                          defaultValue={material?.url}
                        />
                      </label>
                      <label>
                        <input
                          type="checkbox"
                          name="published"
                          defaultChecked={material?.published}
                        />
                        انتشار برای سفیران
                      </label>
                      <div className="amb-row">
                        <button disabled={busy}>ذخیره محتوا</button>
                        {material && (
                          <button
                            type="button"
                            className="amb-secondary"
                            onClick={() => setMaterial(undefined)}
                          >
                            لغو ویرایش
                          </button>
                        )}
                      </div>
                    </form>
                  </section>
                )}
                <section className="amb-card">
                  <h2>مأموریت‌های اختیاری</h2>
                  {resources.missions.map((m) => (
                    <article className="amb-card" key={m.id}>
                      <span className="amb-badge">
                        {m.published ? "منتشرشده" : "پیش‌نویس"}
                      </span>
                      <h3>{m.title}</h3>
                      <p className="amb-pre">هدف: {m.objective}</p>
                      <p className="amb-pre">اقدام پیشنهادی: {m.action}</p>
                      <p>مهلت: {ambassadorDate(m.deadline)}</p>
                      {m.url && (
                        <a href={m.url} target="_blank" rel="noreferrer">
                          لینک مأموریت
                        </a>
                      )}
                      {has("CONTENT") && (
                        <div className="amb-row">
                          <button
                            className="amb-secondary"
                            onClick={() => setMission(m)}
                          >
                            ویرایش
                          </button>
                          <button
                            disabled={busy}
                            className="amb-secondary"
                            onClick={() =>
                              run(() =>
                                ambassadors.mission(
                                  {
                                    title: m.title,
                                    objective: m.objective,
                                    action: m.action,
                                    ...(m.url ? { url: m.url } : {}),
                                    ...(m.deadline
                                      ? { deadline: m.deadline }
                                      : {}),
                                    published: !m.published,
                                  },
                                  m.id,
                                ),
                              )
                            }
                          >
                            {m.published ? "برداشتن از انتشار" : "انتشار"}
                          </button>
                        </div>
                      )}
                    </article>
                  ))}
                  {!resources.missions.length && <p>مأموریتی ثبت نشده است.</p>}
                </section>
                {has("CONTENT") && (
                  <section className="amb-card">
                    <h2>{mission ? "ویرایش مأموریت" : "مأموریت جدید"}</h2>
                    <p>
                      همه مأموریت‌ها اختیاری هستند؛ درخواست تعامل باید واقعی و
                      محترمانه باشد.
                    </p>
                    <form
                      key={mission?.id || `new-mission:${viewVersion}`}
                      onSubmit={(e) => {
                        e.preventDefault();
                        const form = e.currentTarget;
                        const f = fields(form);
                        run(async () => {
                          await ambassadors.mission(
                            {
                              title: f.title,
                              objective: f.objective,
                              action: f.action,
                              url: f.url || null,
                              deadline: f.deadline
                                ? new Date(f.deadline).toISOString()
                                : null,
                              published: f.published === "on",
                            },
                            mission?.id,
                          );
                          setMission(undefined);
                        }, form);
                      }}
                    >
                      <label>
                        عنوان
                        <input
                          name="title"
                          required
                          maxLength={200}
                          defaultValue={mission?.title}
                        />
                      </label>
                      <label>
                        هدف
                        <textarea
                          name="objective"
                          required
                          maxLength={10000}
                          defaultValue={mission?.objective}
                        />
                      </label>
                      <label>
                        اقدام پیشنهادی و شیوه گزارش نتیجه
                        <textarea
                          name="action"
                          required
                          maxLength={10000}
                          defaultValue={mission?.action}
                        />
                      </label>
                      <label>
                        لینک
                        <input
                          type="url"
                          name="url"
                          dir="ltr"
                          defaultValue={mission?.url}
                        />
                      </label>
                      <label>
                        مهلت (ساعت دستگاه)
                        <input
                          type="datetime-local"
                          name="deadline"
                          defaultValue={datetime(mission?.deadline)}
                        />
                      </label>
                      <label>
                        <input
                          type="checkbox"
                          name="published"
                          defaultChecked={mission?.published}
                        />
                        انتشار
                      </label>
                      <div className="amb-row">
                        <button disabled={busy}>ذخیره مأموریت</button>
                        {mission && (
                          <button
                            type="button"
                            className="amb-secondary"
                            onClick={() => setMission(undefined)}
                          >
                            لغو ویرایش
                          </button>
                        )}
                      </div>
                    </form>
                  </section>
                )}
              </>
            )}
            {tab === "staff" && has("SUPERVISOR") && (
              <section className="amb-card">
                <h2>تیم سفیران</h2>
                <p>
                  یک نفر می‌تواند چند مسئولیت داشته باشد. پشتیبان فقط پرونده‌های
                  تخصیص‌یافته خودش را می‌بیند. سرپرست به تمام بخش‌های ماژول
                  دسترسی دارد.
                </p>
                {staff.map((s) => (
                  <div className="amb-check" key={s.userId}>
                    <strong>{ambassadorPerson(s.user)}</strong>
                    <p>
                      {s.roles.map((r) => ambassadorRoles[r]).join(" · ") ||
                        "بدون دسترسی"}
                    </p>
                    <button
                      type="button"
                      disabled={busy}
                      className="amb-secondary"
                      onClick={() => setStaffEditing(s)}
                    >
                      ویرایش مسئولیت‌ها
                    </button>
                  </div>
                ))}
                <form
                  key={`staff:${staffEditing?.userId || ""}:${viewVersion}`}
                  onSubmit={(e) => {
                    e.preventDefault();
                    const form = e.currentTarget;
                    const f = new FormData(form);
                    const roles = f.getAll("roles").map(String);
                    setConfirmation({
                      title: `بازبینی مسئولیت‌های ${form.querySelector<HTMLSelectElement>('select[name="userId"]')?.selectedOptions[0]?.textContent || "عضو تیم"}`,
                      description: roles.length
                        ? `مسئولیت‌های انتخاب‌شده جایگزین دسترسی فعلی می‌شوند: ${roles.map((r) => ambassadorRoles[r]).join("، ")}.`
                        : "همه دسترسی‌های این فرد به ماژول سفیران حذف می‌شوند.",
                      label: roles.length ? "ذخیره مسئولیت‌ها" : "حذف دسترسی",
                      danger: !roles.length,
                      action: () =>
                        run(async () => {
                          await ambassadors.setStaff(
                            String(f.get("userId")),
                            roles,
                          );
                          setStaffEditing(undefined);
                        }, form),
                    });
                  }}
                >
                  <h3>ثبت یا جایگزینی دسترسی</h3>
                  <div>
                    <span>شناسه کاربر سایت</span>
                    <AmbassadorUserPicker
                      name="userId"
                      required
                      defaultValue={staffEditing?.userId}
                      defaultLabel={ambassadorPerson(staffEditing?.user)}
                    />
                  </div>
                  {Object.entries(ambassadorRoles).map(([k, v]) => (
                    <label key={k}>
                      <input
                        type="checkbox"
                        name="roles"
                        value={k}
                        defaultChecked={staffEditing?.roles.includes(k)}
                      />
                      {v}
                    </label>
                  ))}
                  <p className="amb-muted">
                    انتخاب‌ها جایگزین دسترسی قبلی می‌شوند. انتخاب نکردن هیچ نقش،
                    دسترسی را حذف می‌کند. برای حذف دسترسی پشتیبان، ابتدا
                    پرونده‌های باز او را واگذار کنید.
                  </p>
                  <button disabled={busy}>ذخیره دسترسی</button>
                </form>
              </section>
            )}
          </>
        )
      )}
      {!!context?.roles.length && (
        <OperatorGuide
          open={guideOpen}
          roles={context.roles}
          onClose={() => setGuideOpen(false)}
        />
      )}
      <ConfirmDialog
        confirmation={confirmation}
        busy={busy}
        onClose={() => setConfirmation(null)}
      />
    </main>
  );
}
